import type { SeriesOptionsType } from 'highcharts';
import { compact, zip } from 'lodash';
import isNil from 'lodash/isNil';
import { useRecoilValue } from 'recoil';
import type { GrowthSimulationPublicPrivateResponse } from 'venn-api';
import {
  allSubjectsAsOfDates,
  blockLimitedRequestSubjects,
  blockPrivateAssetAllocation,
  blockPrivateAssetAllocationVisibility,
  blockSubjectHasProxyState,
  blockYAxisUnitFormat,
  UnitFormat,
} from 'venn-state';
import { useGetRequestSubjectFee } from '../../../../../blocks/utils/hooks/use-advisory-fee';
import { useColorTheme } from 'venn-ui-kit';
import { assert, assertNotNil, Dates, getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import { formatSubjectWithOptionalFee, formatExportableSubjectWithOptionalFee } from '../../../../../legend';
import {
  useGetBlockSubjectSuperscripts,
  usePageProxySymbol,
} from '../../../../../blocks/components/core/block-subject-footnotes-and-superscripts';
import { useBlockId } from '../../../../contexts/BlockIdContext';
import { useConfidenceLevels } from '../../../../logic/useConfidenceLevels';
import { useSubjectColors } from '../../../../logic/useSubjectColors';
import type { Serie } from '../../../charts/types';
import {
  confidenceKeyFromPercentage,
  convertToTimeseries,
  FUNDING_FAILURE_POINT_PREFIX,
  seriesNameFromPercentage,
} from '../utils';
import { toTypeUnsafeSubject } from '../../../../logic/exportUtils';

export const usePublicPrivateGrowthNAVBreakdownChartData = (data: GrowthSimulationPublicPrivateResponse[]) => {
  const blockId = useBlockId();
  const subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const yAxisFormat = useRecoilValue(blockYAxisUnitFormat(blockId));
  const confidenceLevel = assertNotNil(useConfidenceLevels()?.[0]);
  const targetAllocation = useRecoilValue(blockPrivateAssetAllocation(blockId));
  const isTargetAllocationVisible = useRecoilValue(blockPrivateAssetAllocationVisibility(blockId));
  const colors = useColorTheme();
  const proxySymbol = usePageProxySymbol();
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(blockId));
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const confidenceLevelKey = confidenceKeyFromPercentage(confidenceLevel.level * 100);
  const subjectColors = useSubjectColors(subjects.map(getAnalyzedSubjectFromRequestSubject));

  const publicSubject = assertNotNil(subjects.filter((subject) => !subject.private)[0]);
  const privateSubject = assertNotNil(subjects.filter((subject) => subject.private)[0]);

  // It is guaranteed here that there are exactly two subjects: one public, one private, and the public comes first
  // The response format is a bit weird, private NAV is always attached to all public subjects, but for the private subject itself
  // the endpoint returns null. The simplest thing is to just read the data from the first index.
  const publicData = convertToTimeseries(
    (yAxisFormat === UnitFormat.PERCENT ? data[0]!.publicNavPercentage : data[0]!.publicNav)?.[confidenceLevelKey],
    1.0,
  );
  const privateData = convertToTimeseries(
    (yAxisFormat === UnitFormat.PERCENT ? data[0]!.privateNavPercentage : data[0]!.privateNav)?.[confidenceLevelKey],
    1.0,
  );

  const targetAllocationLine = isTargetAllocationVisible
    ? convertToTimeseries(
        data[0]!.publicAndPrivateNav?.[confidenceLevelKey],
        targetAllocation,
        yAxisFormat === UnitFormat.PERCENT ? 1.0 : undefined,
      )
    : null;
  const asOfDates = useRecoilValue(
    allSubjectsAsOfDates(subjects.map((subject) => [blockId, toTypeUnsafeSubject(subject)])),
  ).map(({ asOfDate }) => asOfDate);

  const seriesWithExportable = compact(
    zip(asOfDates, subjects).map(([asOfDate, subject], index) => {
      assert(subject);
      const subjectData = subject.private ? privateData : publicData;
      if (isNil(subjectData)) {
        return null;
      }
      const superscripts = getBlockSubjectSuperscripts({ subject, isBenchmark: false });
      const hasProxy = !!subjectHasProxy[subject.id];
      const formattedName = formatSubjectWithOptionalFee(
        subject,
        proxySymbol,
        hasProxy,
        superscripts,
        getRequestSubjectFee({ subject }),
      );
      const exportableName = formatExportableSubjectWithOptionalFee(
        subject,
        hasProxy,
        getRequestSubjectFee({ subject }),
      );
      const serie: Serie = {
        name: asOfDate ? `${formattedName} NAV (As of ${Dates.toDayMonthYear(asOfDate)})` : `${formattedName} NAV`,
        data: subjectData,
        color: subjectColors[index],
        type: 'column',
      };
      const exportableSerie: Serie = {
        name: asOfDate ? `${exportableName} NAV (As of ${Dates.toDayMonthYear(asOfDate)})` : `${exportableName} NAV`,
        data: subjectData,
        color: subjectColors[index],
        type: 'column',
      };

      return { serie, exportableSerie };
    }),
  );

  const series = seriesWithExportable
    .map(({ serie }) => serie)
    .concat(
      isNil(targetAllocationLine)
        ? []
        : [
            {
              name: 'Target Private Allocation' as const,
              data: targetAllocationLine,
              color: colors.colors.Black,
              type: 'line',
            },
          ],
    );

  const fundingFailure = data[0]?.fundingFailures?.[confidenceLevelKey];
  const mixedNav = data[0]?.publicAndPrivateNav?.[confidenceLevelKey];
  const lastSuccessfulData = mixedNav?.[mixedNav.length - 1];

  const fundingFailureSeries: SeriesOptionsType[] = !isNil(fundingFailure)
    ? [
        {
          type: 'column',
          showInLegend: false,
          color: colors.colors.Error,
          hideFromSharedTooltip: true,
          data: [
            // This fake datapoint with value of 0 is needed to fix resizing issues in RL
            {
              x: lastSuccessfulData?.[0],
              y: 0,
            },
            {
              id: `${FUNDING_FAILURE_POINT_PREFIX}_${seriesNameFromPercentage(confidenceLevel.level)}`,
              x: fundingFailure.date,
              y: yAxisFormat === UnitFormat.PERCENT ? 1 : lastSuccessfulData?.[1],
              metadata: {
                fundingFailure,
                confidenceLevel,
                publicSubject,
                privateSubject,
              },
            },
          ],
        },
      ]
    : [];

  const exportableSeries = seriesWithExportable
    .map(({ exportableSerie }) => exportableSerie)
    .concat(
      isNil(targetAllocationLine)
        ? []
        : [
            {
              name: 'Target Private Allocation' as const,
              data: targetAllocationLine,
              color: colors.colors.Black,
              type: 'line',
            },
          ],
    );

  return {
    series,
    exportableSeries,
    fundingFailureSeries,
  };
};
